<script setup>
// Aviso de presencia · 2026-05-26 (Victor): muestra, encima del cuadro de
// Escribir/Nota, qué OTRO agente está dentro del chat. Dos estados:
//   · viendo      → punto verde latiente · "X también está viendo este chat"
//   · escribiendo → tres puntos ámbar     · "X está escribiendo…"
// Nunca bloquea: es solo informativo para que dos agentes no se pisen.
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

const store = useStore();
const getters = useStoreGetters();

const currentChat = computed(() => getters.getSelectedChat.value || {});
const currentUserId = computed(() => getters.getCurrentUserID.value);

// Otros agentes con el chat abierto (excluye al usuario actual).
const viewers = computed(() => {
  const id = currentChat.value?.id;
  if (!id) return [];
  const list = store.getters['conversationViewers/getViewers'](id) || [];
  return list.filter(u => u && u.id !== currentUserId.value);
});

// De esos otros agentes, los que están tecleando ahora mismo.
const typingViewers = computed(() => {
  const id = currentChat.value?.id;
  if (!id || !viewers.value.length) return [];
  const typing = store.getters['conversationTypingStatus/getUserList'](id) || [];
  if (!typing.length) return [];
  const typingIds = new Set(typing.map(u => u.id));
  return viewers.value.filter(v => typingIds.has(v.id));
});

const isTyping = computed(() => typingViewers.value.length > 0);
const activeUsers = computed(() =>
  isTyping.value ? typingViewers.value : viewers.value
);

// Frase final según estado + cantidad.
const phrase = computed(() => {
  const n = activeUsers.value.length;
  if (isTyping.value) return n === 1 ? 'está escribiendo…' : 'están escribiendo…';
  return n === 1
    ? 'también está viendo este chat'
    : 'también están viendo este chat';
});

let pruneInterval = null;
onMounted(() => {
  pruneInterval = setInterval(
    () => store.dispatch('conversationViewers/prune'),
    5000
  );
});
onBeforeUnmount(() => {
  if (pruneInterval) clearInterval(pruneInterval);
});
</script>

<template>
  <div
    v-if="activeUsers.length > 0"
    class="flex items-center gap-2.5 pl-[18px] py-3.5 text-[12.5px] font-bold"
    :class="isTyping ? 'text-amber-400' : 'text-emerald-400'"
  >
    <!-- escribiendo · tres puntos ámbar -->
    <span v-if="isTyping" class="flex items-center gap-[3px] shrink-0">
      <span class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:-0.3s]" />
      <span class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce [animation-delay:-0.15s]" />
      <span class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" />
    </span>
    <!-- viendo · punto verde latiente -->
    <span
      v-else
      class="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_9px_rgba(52,211,153,0.85)] animate-pulse shrink-0"
    />

    <span>
      <b class="text-white font-extrabold">{{ activeUsers[0].name }}</b>
      <template v-if="activeUsers.length === 2">
        y <b class="text-white font-extrabold">{{ activeUsers[1].name }}</b>
      </template>
      <template v-else-if="activeUsers.length > 2">
        y {{ activeUsers.length - 1 }} más
      </template>
      {{ phrase }}
    </span>
  </div>
</template>
